import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';
import SettingsIcon from '../../../images/vector/settings.svg';
import { ApolloContext } from '../../../ApolloContextProvider';
import { SPACE } from '../../../constants/keyboardCodes';
import { CAPTIONS, OWL_ANIMATION, DEFAULT_FONT_SIZE_SCALE, MEDIUM_FONT_SIZE_SCALE, MAXIMUM_FONT_SIZE_SCALE } from '../../../constants/accessibility';
import Tooltip from './Tooltip';
import selectors from '../../../rootSelectors';
import { LANGUAGES } from '../../localization/consts';
import isAccessibilityEnabled from '../../utils/commonUtils';
import isAlexEnabled from '../../alex/AlexUtils';

const AccessibilitySettingsDropdown = ({ isMazeLicensed }) => {
    const {
        isLouisianaLicenseAvailable,
        setAccessibilityConfigValues,
        isCaptionsEnabled,
        isAnimationVisualReduced,
        fontSizeScaleValue
    } = useContext(ApolloContext);
    const [accessibilityFontSize, setAccessibilityFontSize] = useState(fontSizeScaleValue);
    const [isDropdownVisible, showDropdown] = useState(false);
    const menuRef = useRef();
    const settingsIconRef = useRef();
    const [loadingState, setLoadingState] = useState({});
    const language = useSelector(selectors.getCurrentLanguage);
    const shouldEnableZoom = (language === LANGUAGES.ENGLISH) && (isAccessibilityEnabled() || (isLouisianaLicenseAvailable && isMazeLicensed));

    useEffect(() => {
        setAccessibilityFontSize(fontSizeScaleValue);
    }, [fontSizeScaleValue]);

    const hideDropdown = (e = null) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        showDropdown(false);
    };

    const handleClickOutside = (event) => {
        if (isDropdownVisible && (
            (menuRef.current && !menuRef.current.contains(event.target)) ||
            (settingsIconRef.current && settingsIconRef.current.contains(event.target))
        )) {
            hideDropdown(event);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [isDropdownVisible]);

    const resetZoomValue = () => {
        if (!shouldEnableZoom) {
            if (fontSizeScaleValue !== DEFAULT_FONT_SIZE_SCALE) {
                setAccessibilityConfigValues({ fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE });
            }
        }
    };

    useEffect(() => {
        resetZoomValue();
    }, [shouldEnableZoom, fontSizeScaleValue]);

    const onDropdownClick = (e) => {
        e.preventDefault();
        if (isDropdownVisible) {
            hideDropdown(e);
        } else {
            showDropdown(true);
        }
    };

    const onDropdownKeyDown = (e) => {
        if (e.keyCode !== SPACE) {
            return;
        }
        e.preventDefault();
        if (isDropdownVisible) {
            hideDropdown(e);
        } else {
            showDropdown(true);
        }
    };

    const toggleLoadingState = (id, state) => {
        setLoadingState(prevState => ({ ...prevState, [id]: state }));
    };

    const toggleCaptionsVisibility = async (val) => {
        toggleLoadingState(CAPTIONS, true);
        try {
            await setAccessibilityConfigValues({ isCaptionsEnabled: val });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to toggle captions:', error);
        } finally {
            toggleLoadingState(CAPTIONS, false);
        }
    };

    const toggleOwlAnimation = async (val) => {
        toggleLoadingState(OWL_ANIMATION, true);
        try {
            await setAccessibilityConfigValues({ isAnimationVisualReduced: !val });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to toggle animation:', error);
        } finally {
            toggleLoadingState(OWL_ANIMATION, false);
        }
    };

    const changeTextSize = (fontSize) => {
        setAccessibilityConfigValues({ fontSizeScaleValue: fontSize });
        setAccessibilityFontSize(fontSize);
    };

    const textSizeOptions = [
        { scale: DEFAULT_FONT_SIZE_SCALE, className: 'btn-s' },
        { scale: MEDIUM_FONT_SIZE_SCALE, className: 'btn-m' },
        { scale: MAXIMUM_FONT_SIZE_SCALE, className: 'btn-l' },
    ];

    return (
        <div className="settings">
            <button className="settings-icon-button" aria-label="settings" ref={settingsIconRef} onClick={onDropdownClick} onKeyDown={onDropdownKeyDown}>
                <SettingsIcon className="settings-icon" />
            </button>
            <div className="menu-container">
                <div ref={menuRef} className={`dropdown-menu ${isDropdownVisible ? 'active' : 'inactive'}`}>
                    <ul>
                        {shouldEnableZoom && (
                            <li>
                                Text Size
                                <div className="text-size-controls">
                                    {textSizeOptions.map(({ scale, className }) => (
                                        <div key={className}>
                                            <button
                                                className={`${className} ${accessibilityFontSize === scale ? 'active' : ''}`}
                                                onClick={() => changeTextSize(scale)}
                                            >
                                                T
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </li>
                        )}
                        <li>
                            Closed Captioning
                            <ToggleSwitch
                                id="captions"
                                isDisabled={loadingState.captions}
                                checked={isCaptionsEnabled}
                                onChange={(val) => toggleCaptionsVisibility(val)}
                            />
                        </li>
                        {isAccessibilityEnabled() && !isAlexEnabled() && (
                            <li>
                                <Tooltip text={isAnimationVisualReduced ? "Enable animation" : "Disable animation"}>
                                    Animation
                                    <ToggleSwitch
                                        id="owlAnimation"
                                        isDisabled={loadingState.owlAnimation}
                                        checked={!isAnimationVisualReduced}
                                        onChange={toggleOwlAnimation}
                                    />
                                </Tooltip>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};
AccessibilitySettingsDropdown.propTypes = {
    isMazeLicensed: PropTypes.bool
};
AccessibilitySettingsDropdown.defaultProps = {
    isMazeLicensed: false
};
export default AccessibilitySettingsDropdown;
